import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ServiceDetail.css';
import {
    FaHeartbeat,
    FaBone,
    FaBaby,
    FaFemale,
    FaUserMd,
    FaCog,
    FaBrain,
    FaUtensils,
    FaLungs,
    FaEye,
    FaHandSparkles,
    FaVial,
    FaPills,
    FaAmbulance,
    FaRunning,
    FaTooth,
    FaMicroscope,
    FaChevronDown,
    FaChevronUp,
    FaCalendarAlt,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaArrowLeft,
    FaStethoscope,
    FaSyringe,
    FaFlask,
    FaXRay,
    FaProcedures,
    FaShieldAlt,
    FaTimes,
    FaCalendarCheck
} from 'react-icons/fa';

function ServiceDetail() {
    const { serviceSlug } = useParams();
    const [expandedFAQs, setExpandedFAQs] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        patientName: '',
        mobileNumber: '',
        department: serviceSlug || '',
        appointmentDate: '',
        appointmentTime: ''
    });

    const toggleFAQ = (index) => {
        setExpandedFAQs(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.patientName || !formData.mobileNumber || !formData.appointmentDate || !formData.appointmentTime) {
            alert('Please fill in all required fields.');
            return;
        }

        // Get department name from service data
        const departmentName = service ? service.name : formData.department;

        // Create WhatsApp message
        const message = `New Appointment Request:\n\nPatient Name: ${formData.patientName}\nMobile: ${formData.mobileNumber}\nDepartment: ${departmentName}\nDate: ${formData.appointmentDate}\nTime: ${formData.appointmentTime}`;

        // WhatsApp URL
        const whatsappUrl = `https://wa.me/8374062188?text=${encodeURIComponent(message)}`;

        // Open WhatsApp
        window.open(whatsappUrl, '_blank');

        // Close modal and reset form
        setShowModal(false);
        setFormData({
            patientName: '',
            mobileNumber: '',
            department: serviceSlug || '',
            appointmentDate: '',
            appointmentTime: ''
        });
    };

    const departments = [
        { value: 'cardiology', label: 'Cardiology' },
        { value: 'orthopaedics', label: 'Orthopaedics' },
        { value: 'pediatrics-neonatal-care', label: 'Pediatrics and Neonatal Care' },
        { value: 'obstetrics-gynaecology', label: 'Obstetrics and Gynaecology' },
        { value: 'general-surgery', label: 'General Surgery' },
        { value: 'neurology', label: 'Neurology' },
        { value: 'gastroenterology', label: 'Gastroenterology' },
        { value: 'pulmonology', label: 'Pulmonology' },
        { value: 'ent', label: 'ENT (Ear, Nose, Throat)' },
        { value: 'dermatology', label: 'Dermatology' },
        { value: 'urology-nephrology', label: 'Urology and Nephrology' },
        { value: 'clinical-laboratory', label: '24/7 Clinical Laboratory' },
        { value: 'pharmacy', label: '24/7 Pharmacy' },
        { value: 'emergency-trauma-care', label: 'Emergency and Trauma Care' },
        { value: 'physiotherapy-rehabilitation', label: 'Physiotherapy and Rehabilitation' },
        { value: 'dentistry', label: 'Dentistry' },
        { value: 'oncology', label: 'Oncology' },
        { value: 'general-services', label: 'General Services' }
    ];

    const serviceData = {
        'cardiology': {
            name: 'Cardiology',
            icon: <FaHeartbeat />,
            detailImage: '/src/assets/images/services/cardiology.jpg',
            description: 'Our Cardiology department provides comprehensive heart care with state-of-the-art technology and experienced cardiologists. We offer preventive care, diagnostic services, and advanced treatments for all cardiovascular conditions.',
            detailedDescription: 'At JDS Hospital, our cardiology team is dedicated to providing exceptional heart care. We utilize the latest diagnostic tools including 3D echocardiography, cardiac MRI, and advanced catheterization labs. Our multidisciplinary approach ensures comprehensive care from prevention to complex interventions.',
            conditions: [
                'Coronary Artery Disease',
                'Heart Failure',
                'Arrhythmias',
                'Valvular Heart Disease',
                'Hypertension',
                'Cardiomyopathy',
                'Peripheral Artery Disease'
            ],
            services: [
                'Cardiac Catheterization',
                'Angioplasty & Stenting',
                'Pacemaker Implantation',
                'Heart Surgery',
                'Cardiac Rehabilitation',
                'Preventive Cardiology',
                'Emergency Cardiac Care'
            ],
            tips: [
                'Maintain a healthy diet low in saturated fats and cholesterol',
                'Exercise regularly for at least 30 minutes most days',
                'Monitor blood pressure and cholesterol levels',
                'Avoid smoking and limit alcohol consumption',
                'Manage stress through relaxation techniques',
                'Get regular cardiac check-ups'
            ],
            faqs: [
                {
                    question: 'What should I expect during my first cardiology appointment?',
                    answer: 'During your first visit, our cardiologist will review your medical history, perform a physical examination, and may order diagnostic tests such as ECG, echocardiogram, or stress testing based on your symptoms and risk factors.'
                },
                {
                    question: 'How often should I have my heart checked?',
                    answer: 'The frequency of cardiac check-ups depends on your age, risk factors, and existing conditions. Generally, adults should have their blood pressure checked annually, and those with risk factors may need more frequent monitoring.'
                },
                {
                    question: 'What are the symptoms of a heart attack?',
                    answer: 'Common symptoms include chest pain or discomfort, shortness of breath, nausea, lightheadedness, pain in the arms, back, neck, or jaw. If you experience these symptoms, seek immediate medical attention.'
                },
                {
                    question: 'Can heart disease be prevented?',
                    answer: 'Yes, many heart diseases can be prevented through lifestyle modifications including healthy diet, regular exercise, smoking cessation, stress management, and regular medical check-ups. Early detection and treatment of risk factors like hypertension and diabetes also play crucial roles.'
                }
            ]
        },
        'orthopaedics': {
            name: 'Orthopaedics',
            icon: <FaBone />,
            detailImage: '/src/assets/images/services/orthopaedics.jpg',
            description: 'Expert care for bones, joints, and musculoskeletal issues with advanced surgical and non-surgical treatments.',
            detailedDescription: 'Our Orthopaedic department specializes in the diagnosis, treatment, and rehabilitation of injuries and disorders of the musculoskeletal system. From sports injuries to degenerative conditions, our team provides comprehensive care using the latest techniques and technologies.',
            conditions: [
                'Arthritis',
                'Sports Injuries',
                'Fractures',
                'Joint Disorders',
                'Back Pain',
                'Spinal Disorders',
                'Limb Deformities'
            ],
            services: [
                'Joint Replacement Surgery',
                'Arthroscopic Surgery',
                'Spinal Surgery',
                'Sports Medicine',
                'Physical Therapy',
                'Pain Management',
                'Trauma Surgery'
            ],
            tips: [
                'Maintain proper posture while sitting and standing',
                'Exercise regularly to strengthen muscles and joints',
                'Maintain healthy body weight to reduce joint stress',
                'Use proper lifting techniques',
                'Wear appropriate footwear',
                'Take breaks during repetitive activities'
            ],
            faqs: [
                {
                    question: 'When should I see an orthopedic specialist?',
                    answer: 'You should consult an orthopedic specialist if you experience persistent joint pain, difficulty moving, swelling, deformity, or if an injury doesn\'t improve with rest and basic care.'
                },
                {
                    question: 'What is the recovery time for joint replacement surgery?',
                    answer: 'Recovery time varies depending on the type of surgery and individual factors. Generally, patients can expect 3-6 months for full recovery, with most returning to normal activities within 4-6 weeks.'
                },
                {
                    question: 'Can arthritis be cured?',
                    answer: 'While arthritis cannot be completely cured, it can be effectively managed through medication, physical therapy, lifestyle changes, and in some cases, surgical interventions to reduce pain and improve function.'
                }
            ]
        },
        'pediatrics-neonatal-care': {
            name: 'Pediatrics and Neonatal Care',
            icon: <FaBaby />,
            detailImage: '/src/assets/images/services/pediatrics.jpg',
            description: 'Specialized care for children and newborns with compassionate, family-centered healthcare services.',
            detailedDescription: 'Our Pediatrics department provides comprehensive healthcare for infants, children, and adolescents. From routine wellness visits to complex medical conditions, our pediatricians and specialists ensure the healthy development and well-being of your children.',
            conditions: [
                'Childhood Infections',
                'Developmental Disorders',
                'Asthma',
                'Allergies',
                'Growth Issues',
                'Behavioral Problems',
                'Genetic Disorders'
            ],
            services: [
                'Well-Child Visits',
                'Immunizations',
                'Neonatal Intensive Care',
                'Pediatric Surgery',
                'Developmental Assessments',
                'Nutrition Counseling',
                'Emergency Pediatric Care'
            ],
            tips: [
                'Ensure regular well-child check-ups',
                'Keep up with recommended vaccinations',
                'Promote healthy eating habits',
                'Encourage physical activity',
                'Monitor developmental milestones',
                'Create a safe home environment'
            ],
            faqs: [
                {
                    question: 'How often should my child see a pediatrician?',
                    answer: 'Children should have well-child visits at birth, 3-5 days, 1 month, 2 months, 4 months, 6 months, 9 months, 12 months, 15 months, 18 months, 24 months, and then annually until age 21.'
                },
                {
                    question: 'What should I do if my child has a fever?',
                    answer: 'For infants under 3 months with fever over 100.4°F, seek immediate medical attention. For older children, monitor temperature, ensure hydration, and consult a doctor if fever persists over 3 days or is accompanied by other symptoms.'
                },
                {
                    question: 'When should I be concerned about my child\'s development?',
                    answer: 'Contact your pediatrician if your child doesn\'t reach developmental milestones, has persistent behavioral issues, shows signs of developmental delay, or if you have concerns about speech, motor skills, or social development.'
                }
            ]
        },
        'obstetrics-gynaecology': {
            name: 'Obstetrics and Gynaecology',
            icon: <FaFemale />,
            description: 'Complete women\'s health services including pregnancy care, gynecological treatments, and reproductive health.',
            detailedDescription: 'Our Obstetrics and Gynecology department provides comprehensive care for women throughout all stages of life. From family planning to menopause management, our specialists offer personalized care with the latest technology and compassionate approach.',
            conditions: [
                'Pregnancy Complications',
                'Menstrual Disorders',
                'Infertility',
                'Menopause',
                'Pelvic Disorders',
                'Reproductive Cancers',
                'Hormonal Imbalances'
            ],
            services: [
                'Prenatal Care',
                'High-Risk Pregnancy Management',
                'Gynecological Surgery',
                'Family Planning',
                'Menopause Management',
                'Cancer Screening',
                'Infertility Treatment'
            ],
            tips: [
                'Schedule regular gynecological check-ups',
                'Practice safe sex and get regular STI screenings',
                'Maintain healthy weight and exercise regularly',
                'Eat a balanced diet rich in calcium and vitamins',
                'Stay hydrated and manage stress',
                'Know your family medical history'
            ],
            faqs: [
                {
                    question: 'How often should I have a Pap smear?',
                    answer: 'Women aged 21-29 should have a Pap smear every 3 years. Women aged 30-65 should have a Pap smear combined with HPV testing every 5 years, or Pap smear alone every 3 years.'
                },
                {
                    question: 'What are the signs of pregnancy?',
                    answer: 'Common early signs include missed periods, nausea, breast tenderness, fatigue, frequent urination, and food cravings. However, the most reliable way to confirm pregnancy is through a pregnancy test.'
                },
                {
                    question: 'When should I see a gynecologist?',
                    answer: 'You should see a gynecologist for annual check-ups, irregular periods, pelvic pain, abnormal bleeding, pregnancy planning, contraception counseling, or any concerns about reproductive health.'
                }
            ]
        },
        'general-surgery': {
            name: 'General Surgery',
            icon: <FaUserMd />,
            description: 'Advanced surgical procedures for various conditions with minimally invasive techniques and expert care.',
            detailedDescription: 'Our General Surgery department offers comprehensive surgical services using the latest minimally invasive techniques. From emergency procedures to elective surgeries, our experienced surgeons provide safe, effective treatment with excellent outcomes.',
            conditions: [
                'Hernias',
                'Gallbladder Disease',
                'Appendicitis',
                'Thyroid Disorders',
                'Breast Conditions',
                'Gastrointestinal Disorders',
                'Skin and Soft Tissue Conditions'
            ],
            services: [
                'Laparoscopic Surgery',
                'Emergency Surgery',
                'Breast Surgery',
                'Thyroid Surgery',
                'Hernia Repair',
                'Gallbladder Surgery',
                'Endoscopic Procedures'
            ],
            tips: [
                'Maintain a healthy weight',
                'Eat a balanced diet',
                'Exercise regularly',
                'Avoid smoking',
                'Get regular health check-ups',
                'Report unusual symptoms promptly'
            ],
            faqs: [
                {
                    question: 'What is minimally invasive surgery?',
                    answer: 'Minimally invasive surgery uses small incisions and specialized instruments, often with robotic assistance. It typically results in less pain, shorter hospital stays, and faster recovery compared to traditional open surgery.'
                },
                {
                    question: 'How long is the recovery after surgery?',
                    answer: 'Recovery time varies depending on the type of surgery and individual factors. Minimally invasive procedures often allow patients to return home the same day or within 24 hours, with full recovery in 1-4 weeks.'
                },
                {
                    question: 'What should I expect during pre-operative preparation?',
                    answer: 'Pre-operative preparation includes medical evaluation, lab tests, medication review, fasting instructions, and counseling about the procedure. Your surgeon will provide specific instructions based on your surgery type.'
                }
            ]
        },
        'neurology': {
            name: 'Neurology',
            icon: <FaBrain />,
            description: 'Advanced diagnosis and treatment of neurological disorders with cutting-edge technology and expertise.',
            detailedDescription: 'Our Neurology department specializes in the diagnosis and treatment of disorders of the nervous system. From headaches to complex neurological conditions, our neurologists use advanced diagnostic tools and personalized treatment plans.',
            conditions: [
                'Headaches & Migraines',
                'Epilepsy',
                'Stroke',
                'Parkinson\'s Disease',
                'Multiple Sclerosis',
                'Neuropathy',
                'Dementia'
            ],
            services: [
                'Neurological Examinations',
                'EEG & EMG Testing',
                'MRI & CT Scanning',
                'Stroke Care',
                'Epilepsy Management',
                'Movement Disorder Treatment',
                'Neurorehabilitation'
            ],
            tips: [
                'Maintain a healthy lifestyle',
                'Manage stress effectively',
                'Get adequate sleep',
                'Exercise regularly',
                'Eat a brain-healthy diet',
                'Stay mentally active'
            ],
            faqs: [
                {
                    question: 'What are the warning signs of a stroke?',
                    answer: 'Warning signs include sudden numbness or weakness, confusion, trouble speaking or understanding, vision problems, difficulty walking, dizziness, or severe headache. Remember FAST: Face drooping, Arm weakness, Speech difficulty, Time to call emergency services.'
                },
                {
                    question: 'How is epilepsy diagnosed?',
                    answer: 'Epilepsy is diagnosed through detailed medical history, neurological examination, EEG (electroencephalogram), and sometimes MRI or CT scans. Multiple seizures or specific patterns help confirm the diagnosis.'
                },
                {
                    question: 'Can neurological conditions be prevented?',
                    answer: 'While not all neurological conditions can be prevented, maintaining cardiovascular health, avoiding head injuries, managing chronic conditions like diabetes and hypertension, and living a healthy lifestyle can reduce risk.'
                }
            ]
        },
        'gastroenterology': {
            name: 'Gastroenterology',
            icon: <FaUtensils />,
            description: 'Comprehensive care for digestive system and related conditions with advanced endoscopic procedures.',
            detailedDescription: 'Our Gastroenterology department provides expert care for digestive health issues. From routine screenings to complex procedures, our gastroenterologists use the latest technology to diagnose and treat gastrointestinal disorders.',
            conditions: [
                'GERD',
                'Irritable Bowel Syndrome',
                'Inflammatory Bowel Disease',
                'Liver Disease',
                'Pancreatic Disorders',
                'Gallbladder Disease',
                'Colon Polyps & Cancer'
            ],
            services: [
                'Endoscopic Procedures',
                'Colonoscopy',
                'ERCP',
                'Liver Biopsy',
                'Capsule Endoscopy',
                'Nutritional Counseling',
                'Hepatology Services'
            ],
            tips: [
                'Eat a high-fiber diet',
                'Stay hydrated',
                'Exercise regularly',
                'Limit alcohol consumption',
                'Maintain healthy weight',
                'Get regular screenings'
            ],
            faqs: [
                {
                    question: 'When should I get a colonoscopy?',
                    answer: 'Screening colonoscopy is recommended starting at age 45-50, or earlier if you have risk factors like family history of colon cancer, inflammatory bowel disease, or genetic conditions.'
                },
                {
                    question: 'What causes acid reflux?',
                    answer: 'Acid reflux occurs when stomach acid flows back into the esophagus. Common causes include hiatal hernia, obesity, pregnancy, smoking, certain foods, and medications.'
                },
                {
                    question: 'How can I manage irritable bowel syndrome?',
                    answer: 'IBS management includes dietary changes (low FODMAP diet), stress management, regular exercise, adequate sleep, and sometimes medications. Keeping a food diary can help identify triggers.'
                }
            ]
        },
        'pulmonology': {
            name: 'Pulmonology',
            icon: <FaLungs />,
            description: 'Comprehensive respiratory health and lung disease management with advanced diagnostic capabilities.',
            detailedDescription: 'Our Pulmonology department specializes in the diagnosis and treatment of respiratory disorders. From asthma to complex lung diseases, our pulmonologists provide comprehensive care using advanced diagnostic and therapeutic techniques.',
            conditions: [
                'Asthma',
                'COPD',
                'Pneumonia',
                'Lung Cancer',
                'Sleep Apnea',
                'Pulmonary Fibrosis',
                'Pulmonary Embolism'
            ],
            services: [
                'Pulmonary Function Testing',
                'Bronchoscopy',
                'Sleep Studies',
                'Asthma Management',
                'COPD Treatment',
                'Lung Cancer Screening',
                'Critical Care'
            ],
            tips: [
                'Avoid smoking and secondhand smoke',
                'Get flu and pneumonia vaccines',
                'Exercise regularly',
                'Maintain indoor air quality',
                'Use inhalers properly',
                'Monitor symptoms and seek help when needed'
            ],
            faqs: [
                {
                    question: 'What are the symptoms of COPD?',
                    answer: 'Common symptoms include shortness of breath, chronic cough, wheezing, chest tightness, and frequent respiratory infections. Symptoms often worsen over time and can significantly impact quality of life.'
                },
                {
                    question: 'How is asthma diagnosed?',
                    answer: 'Asthma is diagnosed through medical history, physical examination, pulmonary function tests, and sometimes allergy testing. Peak flow monitoring and challenge tests may also be used.'
                },
                {
                    question: 'Can lung function be improved?',
                    answer: 'Yes, lung function can often be improved through smoking cessation, medications, pulmonary rehabilitation, exercise, and avoiding environmental triggers. Early intervention is key to better outcomes.'
                }
            ]
        },
        'ent': {
            name: 'ENT (Ear, Nose, Throat)',
            icon: <FaEye />,
            description: 'Specialized care for ear, nose, and throat issues with advanced diagnostic and treatment options.',
            detailedDescription: 'Our ENT department provides comprehensive care for disorders of the ear, nose, throat, and related structures. From hearing loss to sinus problems, our otolaryngologists offer expert diagnosis and treatment.',
            conditions: [
                'Hearing Loss',
                'Sinusitis',
                'Tonsillitis',
                'Thyroid Disorders',
                'Voice Disorders',
                'Sleep Apnea',
                'Head & Neck Cancers'
            ],
            services: [
                'Hearing Tests',
                'Endoscopic Sinus Surgery',
                'Tonsillectomy',
                'Thyroid Surgery',
                'Voice Therapy',
                'Allergy Treatment',
                'Facial Plastic Surgery'
            ],
            tips: [
                'Protect your ears from loud noises',
                'Maintain good nasal hygiene',
                'Stay hydrated',
                'Avoid smoking',
                'Use humidifiers in dry environments',
                'Seek prompt treatment for infections'
            ],
            faqs: [
                {
                    question: 'When should I see an ENT specialist?',
                    answer: 'See an ENT if you have persistent ear infections, hearing loss, sinus problems, throat issues, voice changes, swallowing difficulties, or head/neck masses.'
                },
                {
                    question: 'What causes chronic sinusitis?',
                    answer: 'Chronic sinusitis can be caused by infections, allergies, nasal polyps, deviated septum, or immune system disorders. Environmental factors like smoke and pollution can also contribute.'
                },
                {
                    question: 'Can hearing loss be treated?',
                    answer: 'Treatment depends on the cause and type of hearing loss. Options include medications, hearing aids, cochlear implants, surgery, or assistive listening devices. Early intervention improves outcomes.'
                }
            ]
        },
        'dermatology': {
            name: 'Dermatology',
            icon: <FaHandSparkles />,
            description: 'Advanced skin care and dermatological services with cosmetic and medical treatments.',
            detailedDescription: 'Our Dermatology department offers comprehensive skin care services combining medical dermatology with cosmetic procedures. From acne treatment to skin cancer screening, our dermatologists provide expert care for all skin conditions.',
            conditions: [
                'Acne',
                'Eczema',
                'Psoriasis',
                'Skin Cancer',
                'Hair Loss',
                'Nail Disorders',
                'Cosmetic Concerns'
            ],
            services: [
                'Skin Cancer Screening',
                'Acne Treatment',
                'Laser Therapy',
                'Cosmetic Procedures',
                'Hair Restoration',
                'Patch Testing',
                'Mohs Surgery'
            ],
            tips: [
                'Use sunscreen daily',
                'Moisturize regularly',
                'Avoid excessive sun exposure',
                'Use gentle skincare products',
                'Stay hydrated',
                'Monitor moles and skin changes'
            ],
            faqs: [
                {
                    question: 'How often should I have a skin cancer screening?',
                    answer: 'Annual skin cancer screenings are recommended for adults, especially those with risk factors like fair skin, family history, or extensive sun exposure. More frequent screenings may be needed based on individual risk.'
                },
                {
                    question: 'What causes acne?',
                    answer: 'Acne is caused by excess oil production, clogged pores, bacteria, and inflammation. Hormonal changes, certain medications, and genetics can also contribute to acne development.'
                },
                {
                    question: 'Can skin conditions be prevented?',
                    answer: 'Many skin conditions can be prevented through proper skincare, sun protection, healthy lifestyle, and early treatment of skin issues. Regular moisturizing and avoiding irritants also help maintain skin health.'
                }
            ]
        },
        'urology-nephrology': {
            name: 'Urology and Nephrology',
            icon: <FaVial />,
            description: 'Specialized care for urinary and kidney-related conditions with advanced treatment options.',
            detailedDescription: 'Our Urology and Nephrology department provides comprehensive care for kidney and urinary tract disorders. From kidney stones to complex renal conditions, our specialists offer advanced diagnostic and treatment services.',
            conditions: [
                'Kidney Stones',
                'Urinary Tract Infections',
                'Kidney Disease',
                'Prostate Disorders',
                'Bladder Problems',
                'Male Infertility',
                'Renal Failure'
            ],
            services: [
                'Kidney Stone Treatment',
                'Dialysis',
                'Prostate Surgery',
                'Bladder Surgery',
                'Kidney Transplant Care',
                'Urinary Incontinence Treatment',
                'Male Reproductive Health'
            ],
            tips: [
                'Stay hydrated',
                'Maintain healthy blood pressure',
                'Control blood sugar levels',
                'Avoid smoking',
                'Exercise regularly',
                'Get regular check-ups'
            ],
            faqs: [
                {
                    question: 'What are the signs of kidney problems?',
                    answer: 'Signs include changes in urination, swelling, fatigue, nausea, loss of appetite, and high blood pressure. Many kidney problems are asymptomatic until advanced stages.'
                },
                {
                    question: 'How can kidney stones be prevented?',
                    answer: 'Prevention includes drinking plenty of water, reducing salt intake, limiting animal protein, and avoiding certain foods. Medications may be prescribed for recurrent stone formers.'
                },
                {
                    question: 'What is dialysis?',
                    answer: 'Dialysis is a treatment that removes waste and excess fluid from the blood when kidneys fail. There are two main types: hemodialysis (machine filters blood) and peritoneal dialysis (abdomen filters blood).'
                }
            ]
        },
        'clinical-laboratory': {
            name: '24/7 Clinical Laboratory',
            icon: <FaMicroscope />,
            description: 'Round-the-clock diagnostic testing services with state-of-the-art equipment and rapid results.',
            detailedDescription: 'Our 24/7 Clinical Laboratory provides comprehensive diagnostic testing services around the clock. With advanced equipment and experienced technicians, we deliver accurate results quickly to support timely medical decisions.',
            conditions: [
                'Infectious Diseases',
                'Hormonal Imbalances',
                'Cardiac Markers',
                'Liver Function',
                'Kidney Function',
                'Blood Disorders',
                'Cancer Markers'
            ],
            services: [
                'Blood Tests',
                'Urine Analysis',
                'Microbiology',
                'Histopathology',
                'Molecular Diagnostics',
                'Emergency Testing',
                'Health Screenings'
            ],
            tips: [
                'Fast before certain blood tests',
                'Bring previous test results',
                'Inform about medications',
                'Stay hydrated',
                'Wear comfortable clothing',
                'Arrive on time for appointments'
            ],
            faqs: [
                {
                    question: 'How long do lab results take?',
                    answer: 'Results vary by test type. Routine blood work is usually available within 24 hours, while specialized tests may take 3-7 days. Emergency tests are processed immediately.'
                },
                {
                    question: 'Do I need to fast before blood tests?',
                    answer: 'Fasting is required for tests like lipid profile, glucose, and liver function tests. Your doctor will specify fasting requirements when ordering tests.'
                },
                {
                    question: 'How accurate are lab tests?',
                    answer: 'Our laboratory maintains strict quality control standards with regular calibration and proficiency testing. Test accuracy depends on proper sample collection and handling.'
                }
            ]
        },
        'pharmacy': {
            name: '24/7 Pharmacy',
            icon: <FaPills />,
            description: 'Always available pharmaceutical services with expert consultation and medication management.',
            detailedDescription: 'Our 24/7 Pharmacy provides round-the-clock access to medications and pharmaceutical care. Our licensed pharmacists offer expert consultation, medication counseling, and ensure safe and effective medication use.',
            conditions: [
                'Chronic Disease Management',
                'Acute Illness Treatment',
                'Preventive Care',
                'Medication Therapy',
                'Drug Interactions',
                'Allergies',
                'Side Effects'
            ],
            services: [
                'Prescription Filling',
                'Medication Counseling',
                'Drug Information',
                'Compounding',
                'Health Screenings',
                'Immunizations',
                'Emergency Medications'
            ],
            tips: [
                'Keep a list of all medications',
                'Take medications as prescribed',
                'Ask about drug interactions',
                'Store medications properly',
                'Dispose of expired medications safely',
                'Get regular medication reviews'
            ],
            faqs: [
                {
                    question: 'Can I get my prescription filled here?',
                    answer: 'Yes, we accept prescriptions from all licensed healthcare providers. Bring your prescription or we can transfer it from another pharmacy.'
                },
                {
                    question: 'Do you offer home delivery?',
                    answer: 'Yes, we provide home delivery services for prescriptions. Contact us to arrange delivery or check if you qualify for our delivery program.'
                },
                {
                    question: 'What should I do if I miss a dose?',
                    answer: 'If you miss a dose, take it as soon as you remember unless it\'s close to the next dose. Never double dose. Contact your doctor or pharmacist for specific advice about your medication.'
                }
            ]
        },
        'emergency-trauma-care': {
            name: 'Emergency and Trauma Care',
            icon: <FaAmbulance />,
            description: 'Immediate care for emergencies and trauma with advanced life support and rapid response.',
            detailedDescription: 'Our Emergency and Trauma Care department provides 24/7 immediate medical care for life-threatening conditions and injuries. With advanced equipment and skilled emergency physicians, we deliver rapid assessment and treatment.',
            conditions: [
                'Heart Attacks',
                'Stroke',
                'Severe Injuries',
                'Respiratory Distress',
                'Severe Infections',
                'Poisoning',
                'Traumatic Injuries'
            ],
            services: [
                'Emergency Assessment',
                'Trauma Care',
                'Cardiac Emergencies',
                'Respiratory Support',
                'Critical Care',
                'Emergency Surgery',
                'Ambulance Services'
            ],
            tips: [
                'Know emergency contact numbers',
                'Have emergency supplies ready',
                'Learn basic first aid',
                'Keep important medical information accessible',
                'Have an emergency plan',
                'Stay calm during emergencies'
            ],
            faqs: [
                {
                    question: 'When should I go to the emergency room?',
                    answer: 'Go to the ER for chest pain, difficulty breathing, severe bleeding, head injuries, poisoning, severe allergic reactions, or any life-threatening symptoms.'
                },
                {
                    question: 'What should I bring to the emergency room?',
                    answer: 'Bring identification, insurance information, list of current medications, allergies, and medical history. If possible, bring a family member who knows your medical history.'
                },
                {
                    question: 'How long will I wait in the emergency room?',
                    answer: 'Wait times vary based on severity. Critical cases are seen immediately. Non-emergency cases may have longer waits. Triage ensures the sickest patients are treated first.'
                }
            ]
        },
        'physiotherapy-rehabilitation': {
            name: 'Physiotherapy and Rehabilitation',
            icon: <FaRunning />,
            description: 'Expert physiotherapy and rehabilitation services to restore mobility and improve quality of life.',
            detailedDescription: 'Our Physiotherapy and Rehabilitation department helps patients recover from injuries, surgeries, and chronic conditions. Our licensed therapists use evidence-based techniques to restore function and prevent future injuries.',
            conditions: [
                'Sports Injuries',
                'Post-Surgical Recovery',
                'Chronic Pain',
                'Neurological Conditions',
                'Orthopedic Issues',
                'Cardiac Rehabilitation',
                'Work-Related Injuries'
            ],
            services: [
                'Physical Therapy',
                'Occupational Therapy',
                'Sports Rehabilitation',
                'Pain Management',
                'Manual Therapy',
                'Exercise Programs',
                'Electrotherapy'
            ],
            tips: [
                'Follow your therapy plan consistently',
                'Communicate with your therapist',
                'Do home exercises as prescribed',
                'Maintain proper posture',
                'Stay active within your limits',
                'Report any increased pain'
            ],
            faqs: [
                {
                    question: 'How long does physical therapy take?',
                    answer: 'Treatment duration varies by condition and individual response. Most patients see improvement within 4-6 weeks, but complete recovery may take several months depending on the injury or condition.'
                },
                {
                    question: 'Does physical therapy hurt?',
                    answer: 'Some discomfort is normal during therapy as you work to improve strength and mobility. However, therapy should not cause severe pain. Always communicate with your therapist about your comfort level.'
                },
                {
                    question: 'Will I need physical therapy after surgery?',
                    answer: 'Many surgeries benefit from post-operative physical therapy to restore strength, mobility, and function. Your surgeon will recommend therapy based on your specific procedure and recovery needs.'
                }
            ]
        },
        'dentistry': {
            name: 'Dentistry',
            icon: <FaTooth />,
            description: 'Complete dental care and treatments with modern technology and comfortable patient experience.',
            detailedDescription: 'Our Dentistry department provides comprehensive oral healthcare services. From preventive care to advanced treatments, our experienced dentists use modern technology to ensure healthy smiles and optimal oral health.',
            conditions: [
                'Tooth Decay',
                'Gum Disease',
                'Tooth Loss',
                'Oral Infections',
                'Jaw Disorders',
                'Oral Cancer',
                'Cosmetic Concerns'
            ],
            services: [
                'Preventive Dentistry',
                'Restorative Dentistry',
                'Cosmetic Dentistry',
                'Oral Surgery',
                'Orthodontics',
                'Periodontal Care',
                'Emergency Dental Care'
            ],
            tips: [
                'Brush twice daily with fluoride toothpaste',
                'Floss daily',
                'Visit dentist every 6 months',
                'Limit sugary foods and drinks',
                'Wear mouthguards for sports',
                'Don\'t use teeth as tools'
            ],
            faqs: [
                {
                    question: 'How often should I visit the dentist?',
                    answer: 'Most adults should visit the dentist every 6 months for routine check-ups and cleanings. However, your dentist may recommend more frequent visits based on your oral health needs.'
                },
                {
                    question: 'What causes tooth sensitivity?',
                    answer: 'Tooth sensitivity can be caused by worn enamel, exposed tooth roots, cavities, cracked teeth, gum recession, or recent dental procedures. Using desensitizing toothpaste can help.'
                },
                {
                    question: 'Is teeth whitening safe?',
                    answer: 'Professional teeth whitening is generally safe when performed by a qualified dentist. Over-the-counter products may be less effective and potentially damaging if not used properly.'
                }
            ]
        },
        'oncology': {
            name: 'Oncology',
            icon: <FaUserMd />,
            description: 'Comprehensive cancer care and treatment with multidisciplinary approach and advanced therapies.',
            detailedDescription: 'Our Oncology department provides comprehensive cancer care from diagnosis to survivorship. Our multidisciplinary team uses the latest treatments and technologies to provide personalized care and support throughout the cancer journey.',
            conditions: [
                'Breast Cancer',
                'Lung Cancer',
                'Colorectal Cancer',
                'Prostate Cancer',
                'Blood Cancers',
                'Skin Cancer',
                'Other Cancers'
            ],
            services: [
                'Cancer Screening',
                'Chemotherapy',
                'Radiation Therapy',
                'Surgical Oncology',
                'Immunotherapy',
                'Palliative Care',
                'Support Services'
            ],
            tips: [
                'Get regular cancer screenings',
                'Maintain healthy lifestyle',
                'Know your family history',
                'Avoid tobacco products',
                'Limit alcohol consumption',
                'Protect skin from sun exposure'
            ],
            faqs: [
                {
                    question: 'What are the early signs of cancer?',
                    answer: 'Early signs vary by cancer type but may include unexplained weight loss, persistent fatigue, unusual bleeding, lumps, changes in bowel habits, or persistent pain. Regular screenings help with early detection.'
                },
                {
                    question: 'What is chemotherapy?',
                    answer: 'Chemotherapy uses drugs to destroy cancer cells. It can be given orally, intravenously, or through other methods. Treatment plans are individualized based on cancer type, stage, and patient health.'
                },
                {
                    question: 'Can cancer be prevented?',
                    answer: 'While not all cancers can be prevented, risk can be reduced through healthy lifestyle choices: not smoking, maintaining healthy weight, regular exercise, balanced diet, limiting alcohol, and getting recommended screenings.'
                }
            ]
        },
        'general-services': {
            name: 'General Services',
            icon: <FaCog />,
            description: 'Comprehensive general medical services providing primary healthcare and wellness support.',
            detailedDescription: 'Our General Services department offers primary healthcare for patients of all ages. From routine check-ups to managing chronic conditions, our general practitioners provide comprehensive care focused on prevention and wellness.',
            conditions: [
                'Common Infections',
                'Hypertension',
                'Diabetes',
                'Respiratory Infections',
                'Digestive Issues',
                'Minor Injuries',
                'Preventive Care'
            ],
            services: [
                'Annual Physicals',
                'Chronic Disease Management',
                'Vaccinations',
                'Health Screenings',
                'Minor Procedures',
                'Health Counseling',
                'Referral Services'
            ],
            tips: [
                'Schedule regular check-ups',
                'Maintain healthy diet',
                'Exercise regularly',
                'Get adequate sleep',
                'Manage stress',
                'Stay up to date on vaccinations'
            ],
            faqs: [
                {
                    question: 'What is included in an annual physical?',
                    answer: 'An annual physical typically includes vital signs, physical examination, health history review, screening tests, vaccinations, and counseling on lifestyle and preventive care.'
                },
                {
                    question: 'How can I manage chronic conditions?',
                    answer: 'Managing chronic conditions involves regular monitoring, medication adherence, lifestyle modifications, regular exercise, healthy diet, and working closely with your healthcare provider for ongoing care.'
                },
                {
                    question: 'What vaccinations do I need?',
                    answer: 'Vaccination needs vary by age, health status, and risk factors. Adults should stay up to date on tetanus, diphtheria, pertussis, influenza, and COVID-19 vaccines. Consult your doctor for personalized vaccination recommendations.'
                }
            ]
        }
    };

    const service = serviceData[serviceSlug];

    if (!service) {
        return (
            <div className="service-detail">
                <div className="container">
                    <h1>Service Not Found</h1>
                    <p>The requested service could not be found.</p>
                    <Link to="/services" className="back-link">
                        <FaArrowLeft /> Back to Services
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="service-detail">
            <div className="hero-section">
                <div className="container">
                    <Link to="/services" className="back-link">
                        <FaArrowLeft /> Back to Services
                    </Link>
                    <div className="hero-content">
                        {service.heroImage && (
                            <div className="hero-image">
                                <img src={service.heroImage} alt={service.name} />
                            </div>
                        )}
                        <div className="hero-text">
                            <div className="service-icon">{service.icon}</div>
                            <h1>{service.name}</h1>
                            <p>{service.description}</p>
                            <button className="book-appointment-btn" onClick={() => setShowModal(true)}>
                                <FaCalendarCheck /> Book Appointment
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="service-content">
                    <section className="detailed-description">
                        <h2>About {service.name}</h2>
                        <p>{service.detailedDescription}</p>
                    </section>

                    <section className="conditions">
                        <h2>Conditions We Treat</h2>
                        <div className="condition-grid">
                            {service.conditions.map((condition, index) => (
                                <div key={index} className="condition-item">
                                    {condition}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="services">
                        <h2>Our Services</h2>
                        <div className="service-grid">
                            {service.services.map((serviceItem, index) => (
                                <div key={index} className="service-item">
                                    {serviceItem}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="health-tips">
                        <h2>Health Tips</h2>
                        <ul className="tips-list">
                            {service.tips.map((tip, index) => (
                                <li key={index}>{tip}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="faq-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            {service.faqs.map((faq, index) => (
                                <div key={index} className={`faq-item ${expandedFAQs[index] ? 'expanded' : ''}`}>
                                    <div
                                        className="faq-question"
                                        onClick={() => toggleFAQ(index)}
                                    >
                                        <span>{faq.question}</span>
                                        <FaChevronDown className="faq-icon" />
                                    </div>
                                    <div className="faq-answer">
                                        <p>{faq.answer}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>


                </div>
            </div>

            {/* Appointment Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Book an Appointment</h3>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="appointment-form">
                            <div className="form-group">
                                <label htmlFor="patientName">Patient Name *</label>
                                <input
                                    type="text"
                                    id="patientName"
                                    name="patientName"
                                    value={formData.patientName}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter your full name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="mobileNumber">Mobile Number *</label>
                                <input
                                    type="tel"
                                    id="mobileNumber"
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter your mobile number"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="department">Department</label>
                                <select
                                    id="department"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleInputChange}
                                >
                                    {departments.map((dept) => (
                                        <option key={dept.value} value={dept.value}>
                                            {dept.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="appointmentDate">Appointment Date *</label>
                                <input
                                    type="date"
                                    id="appointmentDate"
                                    name="appointmentDate"
                                    value={formData.appointmentDate}
                                    onChange={handleInputChange}
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="appointmentTime">Time *</label>
                                <input
                                    type="time"
                                    id="appointmentTime"
                                    name="appointmentTime"
                                    value={formData.appointmentTime}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="submit-btn">
                                    Book Appointment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceDetail;
